import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import { adminApi } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { 
  ArrowLeft, Save, CheckCircle2, XCircle, Sparkles, 
  Wand2, Camera, ExternalLink, Calendar, MapPin,
  DollarSign, Tag, Clock, FileText, Loader2
} from 'lucide-react';
import { LoadingBlock } from '../../components/common/States';

export default function AdminDraftEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [event, setEvent] = useState(null);
  const [formData, setFormData] = useState({});
  const [suggestions, setSuggestions] = useState({ parsing: null, summary: null });
  const [loadingSuggestions, setLoadingSuggestions] = useState({ parsing: false, summary: false });

  useEffect(() => {
    fetchEvent();
  }, [id]);

  const fetchEvent = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getEvents({ limit: 1, event_id: id });
      const evt = res.data.events?.[0];
      if (!evt) {
        toast.error('Event not found');
        navigate('/admin/instagram');
        return;
      }
      setEvent(evt);
      setFormData({
        title: evt.title || '',
        slug: evt.slug || '',
        description: evt.description || '',
        banner_image: evt.banner_image || '',
        start_date: evt.start_date || '',
        end_date: evt.end_date || '',
        event_time: evt.event_time || '',
        venue_name: evt.venue_name || '',
        cover_charge: evt.cover_charge || 0,
        booking_open: evt.booking_open ?? false,
        is_featured: evt.is_featured ?? false,
        access_tier: evt.access_tier || 'open',
      });
    } catch (err) {
      toast.error('Failed to load event');
      navigate('/admin/instagram');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleParseCaption = async () => {
    setLoadingSuggestions(prev => ({ ...prev, parsing: true }));
    try {
      const res = await adminApi.post(`/events/${id}/parse-caption`, {});
      if (res.data.success) {
        setSuggestions(prev => ({ ...prev, parsing: res.data.suggestions }));
        toast.success('Caption parsed successfully');
      } else {
        toast.error(res.data.message || 'Failed to parse caption');
      }
    } catch (err) {
      toast.error('Failed to parse caption');
    } finally {
      setLoadingSuggestions(prev => ({ ...prev, parsing: false }));
    }
  };

  const handleGenerateSummary = async () => {
    setLoadingSuggestions(prev => ({ ...prev, summary: true }));
    try {
      const res = await adminApi.post(`/events/${id}/generate-summary`, {});
      if (res.data.success) {
        setSuggestions(prev => ({ ...prev, summary: res.data.suggestions }));
        toast.success('Summary generated successfully');
      } else {
        toast.error(res.data.message || 'Failed to generate summary');
      }
    } catch (err) {
      toast.error('Failed to generate summary');
    } finally {
      setLoadingSuggestions(prev => ({ ...prev, summary: false }));
    }
  };

  const applySuggestion = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    toast.success(`Applied suggestion to ${field}`);
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await adminApi.updateEvent(id, formData);
      toast.success('Draft saved');
      fetchEvent();
    } catch (err) {
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      await adminApi.updateEvent(id, formData);
      const res = await adminApi.patch(`/events/${id}/publish`, {});
      if (res.data.success) {
        toast.success('Event published successfully');
        navigate('/admin/events');
      } else {
        toast.error('Failed to publish event');
      }
    } catch (err) {
      toast.error('Failed to publish event');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm('Are you sure you want to reject this draft?')) return;
    try {
      const res = await adminApi.patch(`/events/${id}/reject`, {});
      if (res.data.success) {
        toast.success('Draft rejected');
        navigate('/admin/instagram');
      } else {
        toast.error('Failed to reject draft');
      }
    } catch (err) {
      toast.error('Failed to reject draft');
    }
  };

  if (loading) return <AdminShell><LoadingBlock message="Loading draft..." /></AdminShell>;
  if (!event) return <AdminShell><div>Event not found</div></AdminShell>;

  const isInstagramImport = event.source_type === 'instagram';

  return (
    <AdminShell>
      <div className="max-w-6xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate('/admin/instagram')}
              className="text-[#B9B9B9]"
            >
              <ArrowLeft size={16} className="mr-1" />
              Back
            </Button>
            <div>
              <h1 className="font-['EB_Garamond'] text-2xl text-[#F2F2F2] font-semibold">
                Edit Draft Event
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-500 border border-yellow-500/30">
                  Draft
                </span>
                {isInstagramImport && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#E1306C]/15 text-[#E1306C] border border-[#E1306C]/30 flex items-center gap-1">
                    <Camera size={10} /> Instagram Import
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleReject}
              className="border-red-500/30 text-red-500 hover:bg-red-500/10"
            >
              <XCircle size={14} className="mr-1.5" />
              Reject
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleSaveDraft}
              disabled={saving}
              className="border-[#2A2A2A] text-[#B9B9B9]"
            >
              <Save size={14} className="mr-1.5" />
              Save Draft
            </Button>
            <Button
              size="sm"
              className="btn-gold"
              onClick={handlePublish}
              disabled={saving}
            >
              <CheckCircle2 size={14} className="mr-1.5" />
              Publish Event
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Source Info (if Instagram) */}
            {isInstagramImport && (
              <div className="surface-card rounded-xl p-4 border-l-4 border-[#E1306C]">
                <h3 className="text-[#F2F2F2] font-semibold text-sm mb-3 flex items-center gap-2">
                  <Camera size={16} />
                  Instagram Source
                </h3>
                {event.source_media_url && (
                  <div className="mb-3">
                    <img 
                      src={event.source_media_url} 
                      alt="Instagram post"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                )}
                {event.source_caption && (
                  <div className="mb-3">
                    <Label className="text-xs text-[#6F6F6F]">Original Caption</Label>
                    <p className="text-[#B9B9B9] text-sm mt-1 max-h-32 overflow-y-auto">
                      {event.source_caption}
                    </p>
                  </div>
                )}
                {event.source_permalink && (
                  <a 
                    href={event.source_permalink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-[#D4A843] hover:underline flex items-center gap-1"
                  >
                    <ExternalLink size={12} />
                    View on Instagram
                  </a>
                )}
              </div>
            )}

            {/* AI Assistance */}
            <div className="surface-card rounded-xl p-4">
              <h3 className="text-[#F2F2F2] font-semibold text-sm mb-3 flex items-center gap-2">
                <Sparkles size={16} className="text-[#D4A843]" />
                AI Assistance
              </h3>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleParseCaption}
                  disabled={loadingSuggestions.parsing || !event.source_caption}
                  className="border-[#2A2A2A] text-[#B9B9B9] flex-1"
                >
                  {loadingSuggestions.parsing ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Wand2 size={14} className="mr-1.5" />
                  )}
                  Parse Caption
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleGenerateSummary}
                  disabled={loadingSuggestions.summary}
                  className="border-[#2A2A2A] text-[#B9B9B9] flex-1"
                >
                  {loadingSuggestions.summary ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Sparkles size={14} className="mr-1.5" />
                  )}
                  Generate Summary
                </Button>
              </div>
            </div>

            {/* Event Form */}
            <div className="surface-card rounded-xl p-5 space-y-4">
              <h3 className="text-[#F2F2F2] font-semibold mb-2">Event Details</h3>

              {/* Title */}
              <div>
                <Label className="text-xs text-[#B9B9B9]">Event Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="bg-[#111111] border-[#2A2A2A] mt-1"
                  placeholder="Enter event title"
                />
              </div>

              {/* Slug */}
              <div>
                <Label className="text-xs text-[#B9B9B9]">URL Slug *</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => handleChange('slug', e.target.value)}
                  className="bg-[#111111] border-[#2A2A2A] mt-1"
                  placeholder="event-slug"
                />
              </div>

              {/* Description */}
              <div>
                <Label className="text-xs text-[#B9B9B9]">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="bg-[#111111] border-[#2A2A2A] mt-1 min-h-[120px]"
                  placeholder="Event description"
                />
              </div>

              {/* Banner Image */}
              <div>
                <Label className="text-xs text-[#B9B9B9]">Banner Image URL</Label>
                <Input
                  value={formData.banner_image}
                  onChange={(e) => handleChange('banner_image', e.target.value)}
                  className="bg-[#111111] border-[#2A2A2A] mt-1"
                  placeholder="https://..."
                />
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#B9B9B9]">Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => handleChange('start_date', e.target.value)}
                    className="bg-[#111111] border-[#2A2A2A] mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#B9B9B9]">Event Time</Label>
                  <Input
                    type="time"
                    value={formData.event_time}
                    onChange={(e) => handleChange('event_time', e.target.value)}
                    className="bg-[#111111] border-[#2A2A2A] mt-1"
                  />
                </div>
              </div>

              {/* Venue */}
              <div>
                <Label className="text-xs text-[#B9B9B9]">Venue</Label>
                <Input
                  value={formData.venue_name}
                  onChange={(e) => handleChange('venue_name', e.target.value)}
                  className="bg-[#111111] border-[#2A2A2A] mt-1"
                  placeholder="Scratch Unleashed"
                />
              </div>

              {/* Cover Charge */}
              <div>
                <Label className="text-xs text-[#B9B9B9]">Cover Charge (₹)</Label>
                <Input
                  type="number"
                  value={formData.cover_charge}
                  onChange={(e) => handleChange('cover_charge', parseFloat(e.target.value) || 0)}
                  className="bg-[#111111] border-[#2A2A2A] mt-1"
                  placeholder="0"
                />
              </div>

              {/* Access Tier */}
              <div>
                <Label className="text-xs text-[#B9B9B9]">Access Tier</Label>
                <select
                  value={formData.access_tier}
                  onChange={(e) => handleChange('access_tier', e.target.value)}
                  className="w-full bg-[#111111] border border-[#2A2A2A] rounded-lg px-3 py-2 text-sm text-[#F2F2F2] mt-1"
                >
                  <option value="open">Open to All</option>
                  <option value="vip">VIP Only</option>
                  <option value="elite">Elite Only</option>
                </select>
              </div>

              {/* Toggles */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-[#B9B9B9]">Featured Event</Label>
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(v) => handleChange('is_featured', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm text-[#B9B9B9]">Booking Open</Label>
                  <Switch
                    checked={formData.booking_open}
                    onCheckedChange={(v) => handleChange('booking_open', v)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right: Suggestions Panel */}
          <div className="space-y-4">
            {/* Parsing Suggestions */}
            {suggestions.parsing && (
              <div className="surface-card rounded-xl p-4">
                <h3 className="text-[#F2F2F2] font-semibold text-sm mb-3 flex items-center gap-2">
                  <Wand2 size={14} className="text-[#D4A843]" />
                  Parsed Suggestions
                </h3>
                <div className="space-y-3 text-sm">
                  {suggestions.parsing.title && (
                    <SuggestionItem
                      icon={FileText}
                      label="Title"
                      value={suggestions.parsing.title}
                      onApply={() => applySuggestion('title', suggestions.parsing.title)}
                    />
                  )}
                  {suggestions.parsing.date && (
                    <SuggestionItem
                      icon={Calendar}
                      label="Date"
                      value={suggestions.parsing.date}
                      onApply={() => applySuggestion('start_date', suggestions.parsing.date)}
                    />
                  )}
                  {suggestions.parsing.time && (
                    <SuggestionItem
                      icon={Clock}
                      label="Time"
                      value={suggestions.parsing.time}
                      onApply={() => applySuggestion('event_time', suggestions.parsing.time)}
                    />
                  )}
                  {suggestions.parsing.venue && (
                    <SuggestionItem
                      icon={MapPin}
                      label="Venue"
                      value={suggestions.parsing.venue}
                      onApply={() => applySuggestion('venue_name', suggestions.parsing.venue)}
                    />
                  )}
                  {suggestions.parsing.cover_charge !== undefined && (
                    <SuggestionItem
                      icon={DollarSign}
                      label="Cover"
                      value={`₹${suggestions.parsing.cover_charge}`}
                      onApply={() => applySuggestion('cover_charge', suggestions.parsing.cover_charge)}
                    />
                  )}
                  {suggestions.parsing.tags?.length > 0 && (
                    <div className="pt-2 border-t border-[#2A2A2A]">
                      <div className="flex items-center gap-1 text-xs text-[#6F6F6F] mb-2">
                        <Tag size={12} /> Tags
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {suggestions.parsing.tags.map((tag, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-[#2A2A2A] text-[#B9B9B9]">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {suggestions.parsing.confidence && (
                    <div className="text-xs text-[#6F6F6F] pt-2">
                      Confidence: {suggestions.parsing.confidence}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Summary Suggestions */}
            {suggestions.summary && (
              <div className="surface-card rounded-xl p-4">
                <h3 className="text-[#F2F2F2] font-semibold text-sm mb-3 flex items-center gap-2">
                  <Sparkles size={14} className="text-[#D4A843]" />
                  Generated Summary
                </h3>
                <div className="space-y-3 text-sm">
                  {suggestions.summary.summary && (
                    <div>
                      <Label className="text-xs text-[#6F6F6F]">Description</Label>
                      <p className="text-[#B9B9B9] text-xs mt-1 mb-2">
                        {suggestions.summary.summary}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => applySuggestion('description', suggestions.summary.summary)}
                        className="border-[#D4A843]/30 text-[#D4A843] hover:bg-[#D4A843]/10 h-7 text-xs"
                      >
                        Apply
                      </Button>
                    </div>
                  )}
                  {suggestions.summary.tags?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1 text-xs text-[#6F6F6F] mb-2">
                        <Tag size={12} /> Suggested Tags
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {suggestions.summary.tags.map((tag, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-[#2A2A2A] text-[#B9B9B9]">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Preview */}
            <div className="surface-card rounded-xl p-4">
              <h3 className="text-[#F2F2F2] font-semibold text-sm mb-3">Preview</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-[#6F6F6F] text-xs">Title:</span>
                  <p className="text-[#F2F2F2]">{formData.title || 'Untitled'}</p>
                </div>
                <div>
                  <span className="text-[#6F6F6F] text-xs">Date & Time:</span>
                  <p className="text-[#B9B9B9]">{formData.start_date} {formData.event_time || ''}</p>
                </div>
                <div>
                  <span className="text-[#6F6F6F] text-xs">Venue:</span>
                  <p className="text-[#B9B9B9]">{formData.venue_name || 'TBD'}</p>
                </div>
                <div>
                  <span className="text-[#6F6F6F] text-xs">Cover:</span>
                  <p className="text-[#D4A843]">{formData.cover_charge > 0 ? formatCurrency(formData.cover_charge) : 'Free'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}

// Suggestion Item Component
function SuggestionItem({ icon: Icon, label, value, onApply }) {
  return (
    <div className="flex items-start justify-between gap-2 pb-2 border-b border-[#2A2A2A] last:border-0">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 text-xs text-[#6F6F6F] mb-1">
          <Icon size={12} /> {label}
        </div>
        <p className="text-[#F2F2F2] text-sm truncate">{value}</p>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onApply}
        className="border-[#D4A843]/30 text-[#D4A843] hover:bg-[#D4A843]/10 h-7 text-xs flex-shrink-0"
      >
        Apply
      </Button>
    </div>
  );
}
